/**
 * Layout modes (docs/plans/flexui.md): by the space the app has, in CSS px — so a zoomed desktop window gets the
 * layout its width calls for (WCAG 1.4.10). Width: narrow < 600 ≤ medium < 1200 ≤ wide < 1600 ≤ very wide (the
 * same as wide for now). Two adjustments on top: short (height < 500, a phone held sideways) and touch (a coarse
 * pointer). CSS uses the `narrow:`/`mid:`/`wide:`/`vwide:`/`short:`/`touch:` variants; this hook is for what CSS
 * cannot decide — what renders where (a side column, a drawer or a full sheet).
 */
import { useSyncExternalStore } from "react";

export type LayoutMode = "narrow" | "medium" | "wide" | "verywide";

export interface Layout {
  mode: LayoutMode;
  short: boolean;
  touch: boolean;
  /** at least 900 px: room for a running timer's chip in the app bar beside the five places */
  barRoom: boolean;
  /** at least 1800 px: room for every control of the app bar in one row, in every language */
  fullBar: boolean;
}

const QUERIES = {
  medium: "(min-width: 600px)",
  wide: "(min-width: 1200px)",
  verywide: "(min-width: 1600px)",
  short: "(max-height: 499.98px)",
  touch: "(pointer: coarse)",
  barRoom: "(min-width: 900px)",
  fullBar: "(min-width: 1800px)",
} as const;

const mq = (q: string) => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(q) : null);

export function readLayout(): Layout {
  const m = (k: keyof typeof QUERIES) => !!mq(QUERIES[k])?.matches;
  const mode: LayoutMode = m("verywide")
    ? "verywide"
    : m("wide")
      ? "wide"
      : m("medium")
        ? "medium"
        : "narrow";
  return { mode, short: m("short"), touch: m("touch"), barRoom: m("barRoom"), fullBar: m("fullBar") };
}

let current: Layout = readLayout();
let key = JSON.stringify(current);
const subs = new Set<() => void>();

function update() {
  const next = readLayout();
  const k = JSON.stringify(next);
  if (k === key) return;
  current = next;
  key = k;
  mark(next);
  for (const s of subs) s();
}

/** Mirrors the mode onto <html data-layout data-short data-touch> (for CSS and for tests). */
function mark(l: Layout) {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  el.dataset.layout = l.mode;
  el.dataset.short = String(l.short);
  el.dataset.touch = String(l.touch);
}

let listening = false;
function listen() {
  if (listening) return;
  listening = true;
  mark(current);
  for (const q of Object.values(QUERIES)) mq(q)?.addEventListener?.("change", update);
}

export function useLayout(): Layout {
  listen();
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    () => current,
  );
}

/** The page and the task panel side by side (wide and very wide); otherwise the panel goes over the page. */
export const sideBySide = (l: Layout) => l.mode === "wide" || l.mode === "verywide";
